import { useState } from 'react';
import { Link } from 'react-router';
import { UilLock } from '@iconscout/react-unicons';
import { Button } from '../buttons/Button';
import { PlayLectureCard } from '../cards/PlayLectureCard';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import { WeekCompleteCelebration } from './WeekCompleteCelebration';
import API from '../../lib/api';
import { cn } from '../../lib/helpers';
import { Lecture } from '../../lib/types';
import {
    CURRENT_DAY_ANCHOR,
    DIMENSION_STYLES,
    PlanResponse,
    PlanWeek as PlanWeekType,
    themeDimension,
    themeDimensionLabel,
} from '../../lib/plan/types';

// Délka týdne se bere z dat plánu, ne natvrdo — days_per_week se v config/plan.php
// může změnit a spouštěč by se jinak rozešel s realitou. Zamčené týdny přijdou
// bez dnů, proto maximum přes všechny týdny.
function celebrationFor(plan: PlanResponse) {
    if (plan.is_complete) {
        return {
            title: 'Celý plán máte za sebou',
            message:
                'Osm týdnů s třídou je hotových. Můžete se podívat, jak se profil třídy proměnil.',
            intensity: 'plan' as const,
        };
    }

    const daysPerWeek = Math.max(...plan.weeks.map((week) => week.days.length), 0);
    if (daysPerWeek === 0) return null;

    const finishedWeeks = plan.current_position / daysPerWeek;
    if (plan.current_position === 0 || !Number.isInteger(finishedWeeks)) return null;

    // Díky podmínce výše je `finishedWeeks` vždy celé kladné číslo, takže se
    // do textu níže nikdy nedostane desetinná hodnota.
    return {
        title: `Týden ${finishedWeeks} hotový`,
        message: 'Skvělá práce. Pokračujte dalším týdnem, až budete chtít.',
        intensity: 'week' as const,
    };
}

interface PlanWeekProps {
    week: PlanWeekType;
    // Pozice plánu před potvrzením — jediné, proti čemu se dá poznat skutečný
    // posun. Pozice dne se k tomu použít nedá: po úspěšném posunu se jí
    // current_position přesně rovná, takže by oslava nepřišla nikdy.
    currentPosition: number;
    // Proč je týden zamčený: 'purchase' = demo licence (nabídneme koupi),
    // 'progress' = zaplaceno, ale předchozí týden ještě není dohraný.
    lockReason?: 'purchase' | 'progress';
    showUpsell?: boolean;
    onMutate: () => void;
}

export function PlanWeek({
    week,
    currentPosition,
    lockReason = 'purchase',
    showUpsell = false,
    onMutate,
}: PlanWeekProps) {
    // Slug čekající na potvrzení dokončení — pojistka proti omylu z přehrání
    // „na zkoušku": dohrání videa ani „Hotovo" plán neposune bez potvrzení.
    const [confirmSlug, setConfirmSlug] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);
    const [celebration, setCelebration] = useState<{
        title: string;
        message: string;
        intensity: 'week' | 'plan';
    } | null>(null);

    const closeConfirm = () => {
        setConfirmSlug(null);
        setFailed(false);
    };

    const confirmComplete = () => {
        const slug = confirmSlug;
        const positionBefore = currentPosition;
        if (!slug) return;
        setFailed(false);
        // Posun řeší plán, ne lekce — chybějící lekce nesmí uživatele zaseknout.
        API.post<PlanResponse>('/plan/advance', { exercise_id: slug })
            .then((res) => {
                closeConfirm();
                // /plan/advance vrátí 204 bez těla, když uživatel mezitím přišel
                // o aktivní plán — oslavu pak nemá smysl počítat, jen dotáhneme
                // aktuální data.
                if (!res.data) {
                    onMutate();
                    return;
                }
                // Oslavíme jen skutečný posun pozice, ne každé volání —
                // potvrzení už dokončeného dne server ignoruje a oslava by
                // jinak vyskočila bez důvodu.
                if (res.data.current_position > positionBefore) {
                    setCelebration(celebrationFor(res.data));
                }
                onMutate();
            })
            .catch(() => setFailed(true));
    };

    // Předěl mezi týdny — ze seznamu karet dělá etapy. Tenká linka v barvě
    // tématu, u prvního týdne se vynechá, aby plán nezačínal čárou.
    const dimension = themeDimension(week.theme_key);
    const heading = (
        <div className="mb-4">
            {week.week > 1 && (
                <hr
                    className={cn(
                        'mb-6 h-1 rounded-full border-0',
                        dimension
                            ? DIMENSION_STYLES[dimension].card
                            : 'bg-cloud-200'
                    )}
                />
            )}
            <div className="flex flex-wrap items-center gap-3">
                <h4>Týden {week.week}</h4>
                <span
                    className={cn(
                        'rounded-full px-3 py-1 text-xs font-medium text-ink-800',
                        dimension
                            ? DIMENSION_STYLES[dimension].card
                            : 'bg-cloud-200'
                    )}
                >
                    {themeDimensionLabel(week.theme_key)}
                </span>
            </div>
        </div>
    );

    if (week.locked) {
        return (
            <div>
                {heading}
                <div className="flex items-center gap-4 rounded-2xl bg-white p-4 shadow-sm">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cloud-100 text-ink-800">
                        <UilLock size={20} />
                    </div>
                    <div className="opacity-70">
                        <p className="text-ink-800">
                            {themeDimensionLabel(week.theme_key)}
                        </p>
                        {lockReason === 'progress' && (
                            <p className="text-sm text-ink-700">
                                Odemkne se po dokončení předchozího týdne.
                            </p>
                        )}
                    </div>
                </div>

                {lockReason === 'purchase' && showUpsell && (
                    <div className="mt-4 rounded-2xl bg-white p-6 text-center shadow-sm">
                        <p className="mb-4 text-ink-800">
                            Odemkněte všechny týdny a projděte si celý plán bez omezení.
                        </p>
                        <Link to="/koupit" className="inline-block">
                            <Button color="product">Odemkněte celý rok</Button>
                        </Link>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div>
            {heading}
            <div className="grid gap-6">
                {week.days.map((day) => {
                    const lectureProp: Lecture = {
                        name: day.lecture?.name ?? day.exercise_id,
                        methodology: day.lecture?.methodology ?? null,
                        video: day.lecture?.video ?? {
                            url: '',
                            poster: '',
                            length: 0,
                        },
                        // poziční zámek: budoucí den v aktuálním týdnu jen název
                        is_locked: !(day.is_completed || day.is_current),
                        user_completed: day.is_completed,
                        slug: day.exercise_id,
                        // nepoužito kartou; jen musí projít typovou kontrolou
                        category: { name: '', slug: '', color: 'turquoise' },
                    };

                    return (
                        <div
                            key={day.exercise_id}
                            id={day.is_current ? CURRENT_DAY_ANCHOR : undefined}
                            // Aby cvičení po odrolování neskončilo pod lepivou
                            // hlavičkou, kdyby nějaká přibyla.
                            className="scroll-mt-24"
                        >
                            <PlayLectureCard
                                lecture={lectureProp}
                                color="product"
                                isActive={day.is_current}
                                onLessonVideoStart={() => {
                                    API.post(
                                        `/lectures/${day.exercise_id}/started`
                                    );
                                }}
                                onLessonEnd={() =>
                                    setConfirmSlug(day.exercise_id)
                                }
                            />
                        </div>
                    );
                })}
            </div>

            <ConfirmDialog
                isOpen={confirmSlug !== null}
                onClose={closeConfirm}
                onConfirm={confirmComplete}
                heading={failed ? 'Uložení se nezdařilo' : 'Dokončit cvičení?'}
                message={
                    failed
                        ? 'Cvičení se nepodařilo označit jako hotové. Zkuste to prosím znovu.'
                        : 'Označíme cvičení jako hotové a plán se posune na další den.'
                }
                confirmLabel={failed ? 'Zkusit znovu' : 'Ano, hotové'}
                cancelLabel="Zpět"
            />

            {celebration && (
                <WeekCompleteCelebration
                    title={celebration.title}
                    message={celebration.message}
                    intensity={celebration.intensity}
                    onClose={() => setCelebration(null)}
                />
            )}
        </div>
    );
}
